'use client'
import React, { ReactElement, useContext } from 'react'
import { TableHeader } from './TableHeader'
import { TableContext } from '../../../../providers'
import { TableSizes } from '../../../../types'
import { Retain, RetainBehaviorModifier } from '../../../atoms'
import { TableBody } from './TableBody'

export interface TableContainerProps extends React.HTMLAttributes<HTMLDivElement> {
    children?: React.ReactNode
    pagination?: ReactElement
    renderTBodyElement?: boolean
    'data-testid'?: string
}

export const TableContainer = ({ pagination, children, renderTBodyElement, ...props }: TableContainerProps) => {
    const { isScrollable, isInline, isNested, isStretched, size, hideHeader, tableId } = useContext(TableContext)

    const wrapperClasses = (): string => {
        let classes = 'table-component'

        switch (size) {
            case TableSizes.SMALL:
                classes += ' table-component--small'
                break
            case TableSizes.MEDIUM:
                classes += ' table-component--medium'
                break
            case TableSizes.LARGE:
                classes += ' table-component--large'
                break
        }

        if (isNested) classes += ' table-component--nested'
        if (isScrollable) classes += ' table-component--scrollable'
        if (isInline) classes += ' table-component--inline'
        if (isStretched) classes += ' table-component--stretched'

        return classes
    }

    const sizingBoxClasses = (): string => {
        let classes = 'table-component__sizing-box'
        if (isStretched) classes += ' absolute inset-0'
        return classes
    }

    const tableWrapperClasses = (): string => {
        let classes = 'table-component__wrapper'
        if (isInline) classes += ' w-full'
        if (isStretched) classes += ' overflow-auto'
        if (isScrollable) classes += ' h-full'
        return classes
    }

    const paginationClasses = (): string => {
        let classes = 'table-component__pagination relative z-10'
        if (isInline) classes += ' w-full'
        return classes
    }

    return (
        <div className={wrapperClasses()}>
            <div className="table-component__relative-box">
                <div className={sizingBoxClasses()}>
                    <Retain
                        behaviorModifiers={isStretched ? [RetainBehaviorModifier.PADDING_INVERTED] : undefined}
                        className={tableWrapperClasses()}
                        behaviorOnly
                    >
                        <table
                            id={tableId}
                            className={`table-component__table ${props.className ?? ''}`}
                            data-testid={props['data-testid'] ?? null}
                        >
                            {!hideHeader && <TableHeader key="table-header" />}
                            {renderTBodyElement !== false ? <TableBody>{children}</TableBody> : children}
                        </table>
                    </Retain>
                </div>
            </div>

            {pagination && (
                <Retain
                    behaviorModifiers={isStretched ? [RetainBehaviorModifier.PADDING_INVERTED] : undefined}
                    behaviorOnly
                    className={paginationClasses()}
                >
                    {pagination}
                </Retain>
            )}
        </div>
    )
}
